import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { deleteCostCenter, getCostCenters, getPolicies } from '../../../../api/endpoints/corporate';
import type { CostCenter } from '../../../../api/types/corporate';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { CostCenterFormDialog } from '../CostCenterFormDialog';

/** List rendered as a plain MUI Table (not @mui/x-data-grid — incompatible with this
 *  project's pinned TypeScript 4.9.5, see other Phase 1/2 modules for the same note). */
export default function CostCenterListPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<CostCenter | null>(null);
  const [deleting, setDeleting] = useState<CostCenter | null>(null);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const query = useQuery({ queryKey: ['corporate', 'costCenters'], queryFn: () => getCostCenters() });
  // Policies drive the "Politika" select in the form dialog — fetched here so both the
  // create and edit dialog share one cached list instead of each firing its own request.
  const policiesQuery = useQuery({ queryKey: ['corporate', 'policies'], queryFn: () => getPolicies() });

  const rows: CostCenter[] = query.data?.costCenter ?? [];
  const policies = policiesQuery.data?.policies ?? [];

  const deleteMutation = useMutation({
    mutationFn: (costCenterID: number) => deleteCostCenter(costCenterID),
    onSuccess: () => {
      enqueueSnackbar('Masraf merkezi silindi.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['corporate', 'costCenters'] });
      setDeleting(null);
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' }),
  });

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Masraf Merkezleri</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Yeni Masraf Merkezi
        </Button>
      </Stack>

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={24} sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Masraf Merkezi Adı</TableCell>
              <TableCell>Politika</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !query.isFetching && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Kayıtlı masraf merkezi bulunamadı.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.costCenterID} hover>
                <TableCell>{row.costCenterName}</TableCell>
                <TableCell>{row.policyName ?? '—'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditing(row)} aria-label="Düzenle">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleting(row)} aria-label="Sil">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <CostCenterFormDialog
        open={createOpen}
        costCenter={null}
        policies={policies}
        onClose={() => setCreateOpen(false)}
      />
      <CostCenterFormDialog
        open={!!editing}
        costCenter={editing}
        policies={policies}
        onClose={() => setEditing(null)}
      />

      <ConfirmDialog
        open={!!deleting}
        title="Masraf merkezini sil"
        description={`"${deleting?.costCenterName ?? ''}" masraf merkezini silmek istediğinize emin misiniz?`}
        loading={deleteMutation.isPending}
        onConfirm={() => deleting && deleteMutation.mutate(deleting.costCenterID)}
        onCancel={() => setDeleting(null)}
      />
    </Box>
  );
}
